/**
 * Featured Stripe Complete Page
 * 
 * Handles the redirect from Stripe after featured payment
 * Verifies payment status and activates the featured listing
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';

import Loader from '../Loader';
import Link from '../Link';
import { payment } from '../../config';
import messages from '../../locale/messages';
// Pixel Tracking
import { trackSubscribe } from '../../helpers/pixel';

import s from './FeaturedStripeComplete.css';

// Load Stripe
let stripePromise = null;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(payment.stripe.publishableKey);
  }
  return stripePromise;
};

class FeaturedStripeComplete extends Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      status: 'loading',
      paymentIntentId: null,
      errorMessage: null,
    };
  }

  async componentDidMount() {
    await this.checkPaymentStatus();
  }

  checkPaymentStatus = async () => {
    try {
      const stripe = await getStripe();
      
      // Get the payment_intent_client_secret from URL
      const urlParams = new URLSearchParams(window.location.search);
      const clientSecret = urlParams.get('payment_intent_client_secret');
      const paymentIntentId = urlParams.get('payment_intent');

      if (!clientSecret) {
        this.setState({
          status: 'error',
          errorMessage: 'Información de pago no encontrada. Por favor intenta de nuevo.',
        });
        return;
      }

      // Retrieve the PaymentIntent
      const { paymentIntent, error } = await stripe.retrievePaymentIntent(clientSecret);

      if (error) {
        this.setState({
          status: 'error',
          errorMessage: error.message,
        });
        return;
      }

      this.setState({ paymentIntentId: paymentIntent.id });

      // Check the payment status
      switch (paymentIntent.status) {
        case 'succeeded':
          // Payment successful - activate featured listing on server
          await this.activateFeatured(paymentIntent.id);
          break;
        
        case 'processing':
          this.setState({
            status: 'processing',
          });
          break;
        
        case 'requires_payment_method':
          this.setState({
            status: 'failed',
            errorMessage: 'El pago falló. Por favor intenta con otro método de pago.',
          });
          break;
        
        default:
          this.setState({
            status: 'error',
            errorMessage: `Estado de pago inesperado: ${paymentIntent.status}`,
          });
      }
    } catch (error) {
      console.error('[FeaturedStripeComplete] Error:', error);
      this.setState({
        status: 'error',
        errorMessage: error.message || 'Error verificando el pago.',
      });
    }
  };

  activateFeatured = async (paymentIntentId) => {
    try {
      // Get featured data from sessionStorage
      let featuredData = null;
      if (typeof window !== 'undefined') {
        const storedData = sessionStorage.getItem('stripeFeaturedData');
        if (storedData) {
          featuredData = JSON.parse(storedData);
        }
      }

      if (!featuredData || !featuredData.listId) {
        this.setState({
          status: 'error',
          errorMessage: 'Datos de destacado no encontrados. Por favor intenta de nuevo.',
        });
        return;
      }

      // Call backend to activate featured listing
      const response = await fetch('/stripe-featured-confirm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          paymentIntentId,
          featuredData,
        }),
      });

      const result = await response.json();

      if (result.status === 200) {
        // Track Subscribe Event (Meta Pixel & Google Analytics)
        trackSubscribe({
          listingId: featuredData.listId,
          value: featuredData.commissionRate || 0,
          currency: 'USD',
          plan: `Featured Tier ${featuredData.tier || 1}`
        });
        
        // Clear sessionStorage
        if (typeof window !== 'undefined') {
          sessionStorage.removeItem('stripeFeaturedData');
        }
        
        this.setState({
          status: 'success',
        });

        // Redirect to boats page after 3 seconds
        setTimeout(() => {
          window.location.href = '/boats';
        }, 3000);
      } else {
        this.setState({
          status: 'error',
          errorMessage: result.error || 'Error al activar el destacado.',
        });
      }
    } catch (error) {
      console.error('[FeaturedStripeComplete] Error activating featured:', error);
      this.setState({
        status: 'error',
        errorMessage: error.message || 'Error al activar el destacado.',
      });
    }
  };

  render() {
    const { status, errorMessage } = this.state;

    return (
      <div className={s.container}>
        <div className={s.card}>
          {/* Loading State */}
          {status === 'loading' && (
            <div className={s.statusContainer}>
              <div className={s.loaderWrapper}>
                <Loader type="text" />
              </div>
              <h2 className={s.title}>Verificando pago...</h2>
              <p className={s.subtitle}>Por favor espera mientras confirmamos tu pago.</p>
            </div>
          )}

          {/* Processing State */}
          {status === 'processing' && (
            <div className={s.statusContainer}>
              <div className={s.iconWrapper}>
                <div className={s.processingIcon}>⏳</div>
              </div>
              <h2 className={s.title}>Procesando pago</h2>
              <p className={s.subtitle}>
                Tu pago está siendo procesado. Te notificaremos cuando esté completo.
              </p>
              <Link to="/boats" className={s.button}>
                Ir a Mis Botes
              </Link>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className={s.statusContainer}>
              <div className={s.iconWrapper}>
                <div className={s.successIcon}>✓</div>
              </div>
              <h2 className={s.title}>¡Pago Exitoso!</h2>
              <p className={s.subtitle}>
                Tu bote ha sido destacado correctamente. Serás redirigido automáticamente...
              </p>
              <Link to="/boats" className={s.button}>
                Ver Mis Botes
              </Link>
            </div>
          )}

          {/* Failed State */}
          {status === 'failed' && (
            <div className={s.statusContainer}>
              <div className={s.iconWrapper}>
                <div className={s.errorIcon}>✕</div>
              </div>
              <h2 className={s.title}>Pago Fallido</h2>
              <p className={s.subtitle}>{errorMessage}</p>
              <Link to="/boats" className={s.button}>
                Volver a Mis Botes
              </Link>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className={s.statusContainer}>
              <div className={s.iconWrapper}>
                <div className={s.errorIcon}>!</div>
              </div>
              <h2 className={s.title}>Error</h2>
              <p className={s.subtitle}>{errorMessage}</p>
              <Link to="/boats" className={s.button}>
                Volver a Mis Botes
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapState = (state) => ({});

export default injectIntl(
  withStyles(s)(
    connect(mapState)(FeaturedStripeComplete)
  )
);
